/**
 * TeamStrength Hub - Users Management Page
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { orgApi, userApi, invitationApi } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Users, UserPlus, MoreVertical, Search, Shield, UserCog, User as UserIcon,
  AlertTriangle, Trash2, Link2, Copy, Check
} from 'lucide-react';

const UsersPage = () => {
  const { currentOrg } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('member');
  const [adding, setAdding] = useState(false);
  
  // Invitation state
  const [invitations, setInvitations] = useState([]);
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteMaxUses, setInviteMaxUses] = useState('');
  const [inviteExpiresDays, setInviteExpiresDays] = useState(7);
  const [generatedLink, setGeneratedLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [creatingInvite, setCreatingInvite] = useState(false);

  const fetchMembers = async () => {
    if (!currentOrg) return;
    
    try {
      setLoading(true);
      const response = await orgApi.listMembers(currentOrg.org_id);
      setMembers(response.data);
    } catch (error) {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const fetchInvitations = async () => {
    if (!currentOrg) return;
    try {
      const response = await invitationApi.list(currentOrg.org_id);
      setInvitations(response.data);
    } catch (error) {
      console.error('Failed to load invitations');
    }
  };

  useEffect(() => {
    fetchMembers();
    fetchInvitations();
  }, [currentOrg]);

  const handleCreateInvite = async () => {
    setCreatingInvite(true);
    try {
      const response = await invitationApi.create(currentOrg.org_id, {
        org_id: currentOrg.org_id,
        role: inviteRole,
        max_uses: inviteMaxUses ? parseInt(inviteMaxUses) : null,
        expires_days: inviteExpiresDays
      });
      
      const link = `${window.location.origin}/invite/${response.data.token}`;
      setGeneratedLink(link);
      fetchInvitations();
      toast.success('Invitation link created!');
    } catch (error) {
      toast.error('Failed to create invitation');
    } finally {
      setCreatingInvite(false);
    }
  };

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRevokeInvite = async (invitationId) => {
    try {
      await invitationApi.revoke(currentOrg.org_id, invitationId);
      toast.success('Invitation revoked');
      fetchInvitations();
    } catch (error) {
      toast.error('Failed to revoke invitation');
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const response = await userApi.search(searchQuery);
      setSearchResults(response.data);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleAddMember = async () => {
    if (!selectedUser) return;
    
    setAdding(true);
    try {
      await orgApi.addMember(currentOrg.org_id, {
        user_id: selectedUser.user_id,
        org_id: currentOrg.org_id,
        role: selectedRole
      });
      toast.success('Member added!');
      setShowAddDialog(false);
      setSelectedUser(null);
      setSearchQuery('');
      setSearchResults([]);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    } finally {
      setAdding(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await orgApi.updateMemberRole(currentOrg.org_id, userId, newRole);
      toast.success('Role updated!');
      fetchMembers();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!confirm('Are you sure you want to remove this member?')) return;
    
    try {
      await orgApi.removeMember(currentOrg.org_id, userId);
      toast.success('Member removed');
      fetchMembers();
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'org_admin':
        return <Shield className="w-4 h-4 text-violet-500" />;
      case 'team_admin':
        return <UserCog className="w-4 h-4 text-cyan-500" />;
      default:
        return <UserIcon className="w-4 h-4 text-slate-400" />;
    }
  };

  if (!currentOrg) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Users className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <p className="text-slate-500 dark:text-slate-400">Select an organization to manage users</p>
        </div>
      </div>
    );
  }

  if (currentOrg.role !== 'org_admin') {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <p className="text-slate-500 dark:text-slate-400">Only Org Admins can manage users</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>
            Users
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            Manage users in {currentOrg.name}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline"
            onClick={() => setShowInviteDialog(true)}
            data-testid="create-invite-btn"
          >
            <Link2 className="w-4 h-4 mr-2" />
            Invite Link
          </Button>
          <Button 
            className="bg-violet-600 hover:bg-violet-700"
            onClick={() => setShowAddDialog(true)}
            data-testid="add-user-btn"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* Invitation Links */}
      {invitations.length > 0 && (
        <Card className="animate-fade-in bg-violet-50 dark:bg-violet-900/10 border-violet-200 dark:border-violet-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Link2 className="w-4 h-4 text-violet-600" />
              Active Invitation Links
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {invitations.map((inv) => (
                <div key={inv.invitation_id} className="flex items-center justify-between p-3 bg-white dark:bg-slate-900 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <code className="text-xs bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded">
                      {`/invite/${inv.token.slice(0, 8)}...`}
                    </code>
                    <Badge variant="secondary" className="capitalize text-xs">
                      {inv.role.replace('_', ' ')}
                    </Badge>
                    <span className="text-xs text-slate-500">
                      {inv.use_count} used {inv.max_uses ? `/ ${inv.max_uses}` : ''}
                    </span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleRevokeInvite(inv.invitation_id)}
                    className="text-red-500 hover:text-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Members List */}
      <Card className="animate-fade-in stagger-1">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Manrope' }}>Organization Members</CardTitle>
          <CardDescription>{members.length} members</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-16 skeleton rounded-lg" />
              ))}
            </div>
          ) : members.length > 0 ? (
            <div className="space-y-3">
              {members.map((member) => (
                <div 
                  key={member.membership_id}
                  className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center text-violet-600 font-medium">
                      {member.user?.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-slate-700 dark:text-slate-300">
                          {member.user?.name || 'Unknown'}
                        </p>
                        {getRoleIcon(member.role)}
                      </div>
                      <p className="text-sm text-slate-500">{member.user?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="secondary" className="capitalize">
                      {member.role?.replace('_', ' ')}
                    </Badge>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleUpdateRole(member.user_id, 'org_admin')}>
                          <Shield className="w-4 h-4 mr-2" />
                          Make Org Admin
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleUpdateRole(member.user_id, 'team_admin')}>
                          <UserCog className="w-4 h-4 mr-2" />
                          Make Team Admin
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleUpdateRole(member.user_id, 'member')}>
                          <UserIcon className="w-4 h-4 mr-2" />
                          Make Member
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleRemoveMember(member.user_id)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <p className="text-slate-500">No members yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add User Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Add User to Organization</DialogTitle>
            <DialogDescription>
              Search for existing users by email or name
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Search */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by email or name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                  data-testid="user-search-input"
                />
              </div>
              <Button 
                variant="outline"
                onClick={handleSearch}
                disabled={searching}
              >
                {searching ? 'Searching...' : 'Search'}
              </Button>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="border rounded-lg max-h-48 overflow-y-auto">
                {searchResults.map((user) => {
                  const isAlreadyMember = members.find(m => m.user_id === user.user_id);
                  return (
                    <button
                      key={user.user_id}
                      className={`w-full flex items-center gap-3 p-3 hover:bg-slate-50 dark:hover:bg-slate-800 border-b last:border-0 text-left ${
                        selectedUser?.user_id === user.user_id ? 'bg-violet-50 dark:bg-violet-900/20' : ''
                      } ${isAlreadyMember ? 'opacity-50' : ''}`}
                      onClick={() => !isAlreadyMember && setSelectedUser(user)}
                      disabled={isAlreadyMember}
                    >
                      <div className="w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center text-violet-600 text-sm font-medium">
                        {user.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{user.name}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                      {isAlreadyMember && (
                        <Badge variant="secondary" className="text-xs">Already member</Badge>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Selected User */}
            {selectedUser && (
              <div className="p-3 bg-violet-50 dark:bg-violet-900/20 rounded-lg">
                <p className="text-sm font-medium text-violet-700 dark:text-violet-400">
                  Selected: {selectedUser.name} ({selectedUser.email})
                </p>
              </div>
            )}

            {/* Role Selection */}
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger data-testid="role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="team_admin">Team Admin</SelectItem>
                  <SelectItem value="org_admin">Org Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-violet-600 hover:bg-violet-700"
              onClick={handleAddMember}
              disabled={!selectedUser || adding}
              data-testid="confirm-add-user-btn"
            >
              {adding ? 'Adding...' : 'Add User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Invitation Link Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={(open) => {
        setShowInviteDialog(open);
        if (!open) {
          setGeneratedLink('');
          setInviteRole('member');
          setInviteMaxUses('');
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Create Invitation Link</DialogTitle>
            <DialogDescription>
              Generate a link that people can use to join your organization
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {!generatedLink ? (
              <>
                <div className="space-y-2">
                  <Label>Role for invited users</Label>
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="member">Member</SelectItem>
                      <SelectItem value="team_admin">Team Admin</SelectItem>
                      <SelectItem value="org_admin">Org Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Max uses (leave empty for unlimited)</Label>
                  <Input
                    type="number"
                    min="1"
                    placeholder="Unlimited"
                    value={inviteMaxUses}
                    onChange={(e) => setInviteMaxUses(e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Expires in (days)</Label>
                  <Select value={String(inviteExpiresDays)} onValueChange={(v) => setInviteExpiresDays(parseInt(v))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 day</SelectItem>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="90">90 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                  <p className="text-sm font-medium text-emerald-700 dark:text-emerald-400 mb-2">
                    Invitation link created!
                  </p>
                  <div className="flex items-center gap-2">
                    <Input 
                      value={generatedLink} 
                      readOnly 
                      className="text-sm bg-white dark:bg-slate-900"
                    />
                    <Button 
                      variant="outline" 
                      size="icon"
                      onClick={copyToClipboard}
                    >
                      {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-slate-500">
                  Share this link with people you want to invite. They'll be able to create an account and will automatically join your organization.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
              {generatedLink ? 'Done' : 'Cancel'}
            </Button>
            {!generatedLink && (
              <Button 
                className="bg-violet-600 hover:bg-violet-700"
                onClick={handleCreateInvite}
                disabled={creatingInvite}
              >
                {creatingInvite ? 'Creating...' : 'Create Link'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
